import { useCallback, useMemo, useState } from 'react';
import { BaseEdge, EdgeLabelRenderer, EdgeProps, getSmoothStepPath } from 'reactflow';
import { Button } from '@givebutter/primitives/button';
import { faPlus } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { baseNodeDefaults } from 'assets/js/pages/engage/workflows/utils/constants';
import { useReactFlowStore } from 'assets/js/pages/engage/workflows/utils/store';
import { formDefaults } from 'assets/js/pages/engage/workflows/utils/transformers';
import { NodeWithData } from 'assets/js/pages/engage/workflows/utils/types';
import * as Tooltip from 'components/tooltip';
import { WorkflowActionTypeEnum, WorkflowUtilityNodeTypeEnum } from 'constants/workflows';
import { useCurrentWorkflow } from 'customHooks/useCurrentWorkflow';
import { v4 as uuid } from 'uuid';

const AddStepEdge = ({
    id,
    sourceX,
    sourceY,
    targetX,
    targetY,
    target,
    source,
    style,
}: EdgeProps) => {
    const [isTooltipDismissed, setIsTooltipDismissed] = useState(false);
    const { data: workflow } = useCurrentWorkflow();
    const { getNodes, getNode, setNodes, takeSnapshot, lastAddedId } = useReactFlowStore(
        ({ getNodes, getNode, setNodes, takeSnapshot, lastAddedId }) => ({
            getNodes,
            getNode,
            setNodes,
            takeSnapshot,
            lastAddedId,
        }),
    );

    const [edgePath, labelX, labelY] = getSmoothStepPath({
        sourceX,
        sourceY,
        targetX,
        targetY,
        borderRadius: 12,
    });

    const sourceNode = getNode(source);
    const targetNode = getNode(target);

    const shouldShowWaitTooltip = useMemo(() => {
        if (
            isTooltipDismissed ||
            !targetNode?.type ||
            !sourceNode?.type ||
            targetNode.id !== lastAddedId
        ) {
            return false;
        }

        const isBranchTarget = targetNode.type === WorkflowUtilityNodeTypeEnum.BRANCH;
        const isMessageTarget =
            targetNode.type === WorkflowActionTypeEnum.SEND_EMAIL ||
            targetNode.type === WorkflowActionTypeEnum.SEND_SMS;
        const isAwaitableSource = sourceNode.type !== WorkflowUtilityNodeTypeEnum.DELAY;

        const awaitableTargetHasMessage =
            isMessageTarget && targetNode.data && 'messageId' in targetNode.data;

        return (
            targetNode?.type &&
            isAwaitableSource &&
            (isBranchTarget || (isMessageTarget && awaitableTargetHasMessage))
        );
    }, [sourceNode, targetNode, lastAddedId, isTooltipDismissed]);

    const handleClick = useCallback(() => {
        const currentNodes = getNodes();
        const sourceNode = getNode(source);
        const actionNodeUuid = uuid();

        const newNode = {
            ...baseNodeDefaults,
            id: actionNodeUuid,
            type: WorkflowUtilityNodeTypeEnum.CHOOSE_ACTION,
            data: {
                type: WorkflowUtilityNodeTypeEnum.CHOOSE_ACTION,
                parentId: sourceNode?.id ?? null,
            },
            selected: true,
            style: { opacity: 0 },
        } satisfies NodeWithData;

        const newNodes = [
            ...currentNodes.map(node => {
                if (node.id !== target || !node.data) return node;

                return {
                    ...node,
                    data: {
                        ...node.data,
                        parentId: actionNodeUuid,
                    },
                };
            }),
            newNode,
        ];

        if (
            workflow.status !== 'active' ||
            (workflow.status === 'active' && workflow.has_draft_version)
        ) {
            takeSnapshot();
        }

        setNodes(newNodes);
    }, [source, target, setNodes, getNodes, getNode, takeSnapshot, workflow]);

    const handleClickAddDelay = useCallback(() => {
        const currentNodes = getNodes();
        const sourceNode = getNode(source);
        const waitNodeUuid = uuid();

        const newNode = {
            ...baseNodeDefaults,
            id: waitNodeUuid,
            type: WorkflowUtilityNodeTypeEnum.DELAY,
            data: {
                type: WorkflowUtilityNodeTypeEnum.DELAY,
                parentId: sourceNode?.id ?? null,
                formData: {
                    ...formDefaults,
                    values: {
                        preset: '1 day',
                    },
                },
            },
            selected: true,
            style: { opacity: 0 },
        } satisfies NodeWithData;

        const newNodes = [
            ...currentNodes.map(node => {
                if (node.id !== target || !node.data) return node;

                return {
                    ...node,
                    data: {
                        ...node.data,
                        parentId: waitNodeUuid,
                    },
                };
            }),
            newNode,
        ];

        setNodes(newNodes);
    }, [source, target, setNodes, getNodes, getNode]);

    return (
        <>
            <BaseEdge id={id} path={edgePath} style={style} />
            <EdgeLabelRenderer>
                <div
                    className="flex h-16 w-8 items-center justify-center"
                    style={{
                        position: 'absolute',
                        transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
                        pointerEvents: 'all',
                    }}
                >
                    <Tooltip.Root open={shouldShowWaitTooltip}>
                        <Tooltip.Trigger asChild>
                            <Button
                                variant="outline"
                                color="neutral"
                                size="sm"
                                className="nodrag nopan !rounded-full !bg-white shadow-none focus:opacity-100"
                                onClick={handleClick}
                                aria-label="Add step"
                            >
                                <FontAwesomeIcon icon={faPlus} />
                            </Button>
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content inverse side="right">
                                <Tooltip.Arrow inverse />
                                <div className="flex flex-col items-start gap-4">
                                    <div className="flex flex-col gap-1">
                                        <p className="!text-xs font-medium">
                                            💡 Add a time delay between these steps
                                        </p>
                                        <p className="!text-xs">
                                            Give your supporters time to engage with your action
                                            before they move onto the next step!
                                        </p>
                                    </div>
                                    <div className="flex gap-2">
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            color="neutral"
                                            className="!text-gray-700"
                                            onClick={handleClickAddDelay}
                                        >
                                            <FontAwesomeIcon
                                                icon={faPlus}
                                                className="!text-gray-700 [&>path]:!text-gray-700"
                                            />
                                            Add delay
                                        </Button>
                                        <Button
                                            size="sm"
                                            variant="transparent"
                                            onClick={() => setIsTooltipDismissed(true)}
                                        >
                                            Dismiss
                                        </Button>
                                    </div>
                                </div>
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip.Root>
                </div>
            </EdgeLabelRenderer>
        </>
    );
};
export { AddStepEdge };
